import { TFunction } from 'i18next'
import { WallboxHardware } from '@e3dc-react/shell/Libs/EMobility/wallboxes/models/wallbox-hardware.model'
import { ChargingSessionDto, ChargingSessionEvseStatus } from '../services/api/e-mobility'
import { BadgeDto } from '../end-user-settings/badges-for-charging-station/types/badges.types'
import { ControlledDto } from '../installation/api/sub-devices'
import { ChargingSessionModel } from './charging-session-models'

/**
 * @param chargingSessions raw session result
 * @param subDevices all subdevices for a single installation, the only entity containing the user-entered device name. However, it does not contain wallbox id which we can map to session.wallboxID
 * @param wallboxes GET /wallboxes will return the list of all currently installed wallboxes, including their id and mac address, with one additional step, we can map the subdevice to session.wallboxID by matching the mac address
 * @returns
 */
export const toChargingSessions = (
  chargingSessions: Nullable<ChargingSessionDto>[],
  subDevices: Nullable<ControlledDto>[],
  wallboxes: WallboxHardware[],
  chargingCards: BadgeDto[],
  displayEnergyAll: boolean
): ChargingSessionModel[] => {
  const chargingCardsByBadgeId = new Map(chargingCards.map(card => [card.badgeId?.toLowerCase() ?? '', card]))

  return chargingSessions.map(chargingSession => {
    const wallboxId = chargingSession?.wallboxID ?? ''
    const relatedWallbox = wallboxes.find(wallbox => wallbox.serialno === wallboxId)
    const relatedSubDevice = subDevices.find(
      subdevice =>
        parseInt(subdevice?.mediaParameters?.localId ?? '') === relatedWallbox?.number || subdevice.evseSubTypeParameters?.wallboxId === wallboxId
    )

    const {
      id,
      wallboxID,
      sessionID,
      serialno,
      carID,
      authType,
      authData,
      status,
      startAt,
      stopAt,
      chargeTimeActive,
      chargeTimeInactive,
      chargeCounterReadingStart,
      chargeCounterReadingStop,
      receiptData,
      receiptSignature,
      createdAt,
      updatedAt,
      dynamic,
      chargePriceTotal,
      midEnergy,
    } = chargingSession

    const deviceName = relatedSubDevice?.deviceName ?? `ID: ${wallboxID}`

    const dtoEnergySolar = Math.max(parseFloat(chargingSession.energySolar || ''), 0) || 0 // TODO: REMOVE MATH.MAX !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    const dtoEnergyAll = parseFloat(chargingSession.energyAll || '') || 0

    /**
     * Sometimes, the fetched energyAll is higher than energySolar, which is impossible.
     * To account for this, energyAll becomes equal to energySolar when it would be less.
     */
    const correctedEnergyAll = Math.max(dtoEnergyAll, dtoEnergySolar)

    const energyAll = displayEnergyAll || midEnergy ? correctedEnergyAll : correctedEnergyAll - dtoEnergySolar

    const energySolar = displayEnergyAll ? dtoEnergySolar : 0

    const session: ChargingSessionModel = {
      id: id!,
      sessionID: sessionID ?? '',
      serialno: serialno || NaN,
      wallboxID: wallboxID ?? '',
      wallboxNumber: relatedWallbox?.number,
      carID: carID ?? undefined,
      authentication: {
        type: authType || 'NONE',
        data: authData ?? undefined,
        cardName: chargingCardsByBadgeId.get((authData ?? '').toLowerCase())?.name,
      },
      status: (() => {
        const isRfid = authType?.toUpperCase() === 'RFID'

        if (status === 'WAIT_AUTHENTICATION' && isRfid) {
          return 'WAIT_TO_CHARGE'
        }

        return status || 'UNKNOWN'
      })(),
      startAt: startAt ?? undefined,
      stopAt: stopAt ?? undefined,
      durationMilliseconds: (chargeTimeActive || 0) + (chargeTimeInactive || 0),
      chargeTimeActive: chargeTimeActive || 0,
      chargeTimeInactive: chargeTimeInactive || 0,
      counterReadingStart: parseFloat(chargeCounterReadingStart ?? '') || 0,
      counterReadingStop: parseFloat(chargeCounterReadingStop ?? '') || 0,
      energyAll,
      energySolar,
      receiptData: receiptData ?? undefined,
      receiptSignature: receiptSignature ?? undefined,
      createdAt: createdAt || '',
      updatedAt: updatedAt || '',
      deviceName,
      solarRatio: (() => {
        if (!displayEnergyAll) return 0

        if (energyAll === 0 || energySolar === 0) return 0

        const ratio = (energySolar / energyAll) * 100

        return Number(ratio.toFixed(2))
      })(),
      isDynamic: !!dynamic,
      dynamicTariffTotalChargePrice: chargePriceTotal,
      ...getMIDCertificationInformation(chargingSession),
    }

    return session
  })
}

/** Returns the status of a charging session's MID certification */
const getMIDCertificationInformation = (
  session: Nullable<ChargingSessionDto> | undefined
): Pick<ChargingSessionModel, 'isMidCertified' | 'midAnomaly'> => {
  /** If there is no MID certification, anomalies are ignored. */
  const isMidCertified = !!session?.midEnergy
  if (!isMidCertified)
    return {
      isMidCertified: false,
      midAnomaly: undefined,
    }

  /** The MID inconsistent anomaly has priority over the offline anomaly. */
  const isMidInconsistent = !!session?.midEnergyInconsistent
  if (isMidInconsistent)
    return {
      isMidCertified: false,
      midAnomaly: 'InconsistentData',
    }

  const isMidOffline = !!session?.midSessionOffline
  if (isMidOffline)
    return {
      isMidCertified,
      midAnomaly: 'Offline',
    }

  return {
    isMidCertified,
    midAnomaly: undefined,
  }
}

export const getSessionStatusTranslation = (t: TFunction, status: ChargingSessionEvseStatus): string => {
  if (status === 'WAIT_TO_CHARGE') return t('emobility.chargingSession.chargingSessionStatus.WAIT_AUTHENTICATION')

  return t(`emobility.chargingSession.chargingSessionStatus.${status}`)
}
